// A per-tab id, sent on every API request as X-Tab-Id so the server log can
// tell two tabs apart.
//
// The log prefix already carries [sid], the first 8 hex of the JWT jti. That
// is per-login, so two tabs of this app share one token and therefore one sid.
// The case we need to catch is exactly the one sid cannot see: the same task
// open in two tabs, saved from both, the later save silently clobbering the
// earlier. With a tab id, two consecutive saves of one task read as either one
// tab saving twice or two tabs overwriting each other.
//
// sessionStorage, not localStorage: a sessionStorage slot is per-tab by
// definition, and that is the whole mechanism. localStorage or a cookie would
// hand every tab the same id and defeat the purpose. It also survives F5 and
// in-app navigation, so a reload is not mistaken for a second tab -- a
// per-page-load id would make every refresh look like a new tab, which is the
// wrong granularity for this question.
//
// Diagnosis only. Nothing reads this back; it exists to be greppable.

const STORAGE_KEY = 'forus_tab';

// 8 lowercase hex chars, matching the width and shape of the API's own [sid]
// and the web app's `tid` (both /^[a-f0-9]{8}$/). Collisions across the handful
// of tabs one user has open are not a practical concern at this width.
const SHAPE = /^[a-f0-9]{8}$/;

let cached = null;

function mint() {
    const bytes = new Uint8Array(4);
    crypto.getRandomValues(bytes);
    return Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
}

/**
 * The current tab's id: 8 lowercase hex chars, stable for the life of the tab.
 *
 * Every sessionStorage access is guarded -- the accessor itself throws in some
 * privacy modes, and a request must never fail over a log field. When storage
 * is unavailable the id falls back to module scope: still stable while the page
 * lives, just not across a reload, so an F5 reads as a new tab. That fails
 * toward a false positive rather than a missed one, and is rare enough to wear.
 *
 * @return {string} 8 lowercase hex chars.
 */
export function tabId() {
    if (cached) return cached;

    try {
        const stored = sessionStorage.getItem(STORAGE_KEY);
        if (stored && SHAPE.test(stored)) {
            cached = stored;
            return cached;
        }
    } catch (e) {
        // Storage unreadable -- fall through and mint an in-memory id.
    }

    cached = mint();

    try {
        sessionStorage.setItem(STORAGE_KEY, cached);
    } catch (e) {
        // Unwritable for the same reasons. `cached` still holds for this page.
    }

    return cached;
}
